<template>
  <div class="c-slide-viewer w-full h-full relative overflow-hidden">
    <!-- Error State -->
    <div v-if="error" class="absolute inset-0 flex items-center justify-center" :style="{ color: themeVars.error }">
      {{ t('canvasUI.CSlideViewer.error') }}: {{ error }}
    </div>

    <!-- No PDF State -->
    <div
      v-else-if="!hasValidConfig"
      class="no-pdf-state w-full h-full flex items-center justify-center"
      :style="{ color: themeVars.text2, backgroundColor: themeVars.primary6 }"
    >
      <div class="flex flex-col items-center space-y-2">
        <CIcon :color="themeVars.text2" icon="file-image" size="48" />
        <span class="text-sm">{{ t('canvasUI.CSlideViewer.noPdfAvailable') }}</span>
      </div>
    </div>

    <!-- PDF Canvas Container -->
    <div v-else ref="containerRef" class="pdf-container w-full h-full overflow-hidden" :style="containerStyle">
      <canvas ref="canvasRef" class="pdf-canvas" :style="canvasStyle" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

// Initialize PDF.js cache
if (typeof window !== 'undefined') {
  if (!window.__pdfDocumentCache) {
    window.__pdfDocumentCache = new Map()
  }
}

// Cache TTL (10 minutes)
const PDF_CACHE_TTL_MS = 10 * 60 * 1000

const props = withDefaults(
  defineProps<{
    pageIndex?: number
    useClientSide?: boolean
    pdfUrl?: string
  }>(),
  {
    useClientSide: false,
    pageIndex: 0,
    pdfUrl: '',
  },
)

const emit = defineEmits<{
  pdfLoaded: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const error = ref<string | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const isReady = ref(false)
const isLoading = ref(false)
const resizeObserver = ref<ResizeObserver | null>(null)

const hasValidConfig = computed(() => {
  return !!props.pdfUrl && props.pdfUrl.trim() !== ''
})

// Container styling for crisp rendering (instant swap, no fade to avoid flicker)
const containerStyle = computed(() => {
  return {
    position: 'relative' as const,
    width: '100%',
    height: '100%',
    // Keep both guards to avoid any mid-paint glimpse
    opacity: isReady.value ? 1 : 0.001,
    visibility: isReady.value ? ('visible' as const) : ('hidden' as const),
    transition: 'none',
    willChange: 'opacity' as const,
    backfaceVisibility: 'hidden' as const,
    transform: 'translateZ(0)',
    contain: 'paint' as const,
  }
})

// Canvas styling for perfect fit and crisp display
const canvasStyle = computed(() => {
  return {
    position: 'absolute' as const,
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    backgroundColor: themeVars.value.base,
  }
})

function getDocumentUrl(): string {
  return import.meta.env.DEV
    ? props.pdfUrl?.replace(/https:\/\/[^./]+\.my\.pitcher\.com/g, 'http://localhost:3000') || ''
    : props.pdfUrl || ''
}

async function ensurePDFJSLoaded(): Promise<void> {
  if (window.pdfjsLib) return

  const CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149'
  const SCRIPT_ID = 'pdfjs-esm'
  const SCRIPT_SRC = `${CDN}/pdf.min.mjs`
  const WORKER_SRC = `${CDN}/pdf.worker.min.mjs`

  function waitForScript(scriptEl: HTMLScriptElement): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.pdfjsLib) {
        if (!window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SRC
        }
        resolve()
        return
      }
      const onLoad = () => {
        if (!window.pdfjsLib) {
          reject(new Error('PDF.js failed to load'))
          return
        }
        if (!window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SRC
        }
        resolve()
      }
      const onError = () => reject(new Error('Failed to load PDF.js script'))
      scriptEl.addEventListener('load', onLoad, { once: true })
      scriptEl.addEventListener('error', onError, { once: true })
    })
  }

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    await waitForScript(existing)
    return
  }

  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.type = 'module'
  script.src = SCRIPT_SRC
  document.head.appendChild(script)
  await waitForScript(script)
}

async function waitForContainerDimensions(): Promise<void> {
  return new Promise((resolve) => {
    if (!containerRef.value) {
      resolve()
      return
    }

    const checkDimensions = () => {
      const rect = containerRef.value?.getBoundingClientRect()
      if (rect && rect.width > 0 && rect.height > 0) {
        resolve()
      } else {
        requestAnimationFrame(checkDimensions)
      }
    }

    checkDimensions()
  })
}

async function renderPDFPage() {
  try {
    if (!hasValidConfig.value || !canvasRef.value || !containerRef.value) return

    error.value = null
    isLoading.value = true
    isReady.value = false

    await nextTick()
    await waitForContainerDimensions()
    await ensurePDFJSLoaded()
    if (!window.pdfjsLib) {
      console.error('[CSlideViewer.pdfjs] PDF.js is not loaded and we cannot render the PDF')
      return
    }

    const documentUrl = getDocumentUrl()
    const pageNum = (props.pageIndex ?? 0) + 1

    // Try cache with TTL
    const cachedEntry: any | undefined = window.__pdfDocumentCache?.get(documentUrl)
    let pdfDoc: any | undefined
    if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
      pdfDoc = cachedEntry.doc
    } else {
      if (cachedEntry) window.__pdfDocumentCache?.delete(documentUrl)
      const loadingTask = window.pdfjsLib.getDocument({
        url: documentUrl,
        cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149/cmaps/',
        cMapPacked: true,
        enableXfa: false,
        isEvalSupported: false,
        disableRange: true,
        disableStream: true,
        disableAutoFetch: true,
        useSystemFonts: true,
        standardFontDataUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149/standard_fonts/',
      })
      pdfDoc = await loadingTask.promise
      window.__pdfDocumentCache?.set(documentUrl, { doc: pdfDoc, expiresAt: Date.now() + PDF_CACHE_TTL_MS })
    }
    const page = await pdfDoc.getPage(pageNum)

    const containerRect = containerRef.value.getBoundingClientRect()
    if (containerRect.width === 0 || containerRect.height === 0) throw new Error('Container has no dimensions')

    const viewport = page.getViewport({ scale: 1 })
    const devicePixelRatio = window.devicePixelRatio || 1

    // Adaptive DPI scaling
    const baseScale = Math.max(2, devicePixelRatio * 1.5)
    let qualityMultiplier = 1
    const containerSize = Math.min(containerRect.width, containerRect.height)
    if (containerSize < 300) qualityMultiplier = 2.25
    else if (containerSize < 500) qualityMultiplier = 1.75
    else if (containerSize < 800) qualityMultiplier = 1.4
    const scale = baseScale * qualityMultiplier

    const scaleX = containerRect.width / viewport.width
    const scaleY = containerRect.height / viewport.height
    const fitScale = Math.min(scaleX, scaleY)

    // Canvas sizes
    const canvas = canvasRef.value
    const context = canvas.getContext('2d')!
    const displayWidth = containerRect.width
    const displayHeight = (viewport.height / viewport.width) * displayWidth
    const topOffset = Math.max(0, (containerRect.height - displayHeight) / 2)

    canvas.width = Math.round(displayWidth * scale)
    canvas.height = Math.round(displayHeight * scale)
    canvas.style.width = `${displayWidth}px`
    canvas.style.height = `${displayHeight}px`
    canvas.style.top = `${topOffset}px`
    canvas.style.left = '0px'

    // Reset visible context (we'll draw a completed offscreen image to avoid flicker)
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'

    // Prepare offscreen canvas to render PDF fully before swapping
    const offscreen = document.createElement('canvas')
    offscreen.width = canvas.width
    offscreen.height = canvas.height
    const offctx = offscreen.getContext('2d')!
    offctx.imageSmoothingEnabled = true
    offctx.imageSmoothingQuality = 'high'
    offctx.setTransform(1, 0, 0, 1, 0, 0)
    // Background
    offctx.clearRect(0, 0, offscreen.width, offscreen.height)
    offctx.save()
    offctx.fillStyle = themeVars.value.base
    offctx.fillRect(0, 0, offscreen.width, offscreen.height)
    offctx.restore()

    // Render
    const renderContext = {
      canvasContext: offctx,
      // Render at combined scale to the offscreen buffer
      viewport: page.getViewport({ scale: fitScale * scale }),
      enableWebGL: false,
      renderInteractiveForms: false,
      intent: 'display',
      annotationMode: 0,
      optionalContentConfigPromise: null,
    }
    await page.render(renderContext as any).promise

    // Single atomic swap: blit the offscreen buffer to visible canvas
    // Use putImageData to avoid any sampling changes during drawImage
    const imageBitmap = await createImageBitmap(offscreen)
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.drawImage(imageBitmap, 0, 0)

    isLoading.value = false
    // Small delay to let the blit paint before reveal (avoids flicker)
    setTimeout(() => {
      isReady.value = true
      emit('pdfLoaded')
    }, 50)
  } catch (err) {
    console.error('Error rendering PDF page:', err)
    error.value = err instanceof Error ? err.message : 'Error loading PDF'
    isLoading.value = false
    isReady.value = false
  }
}

// Watch for prop changes
watch(
  [() => props.pdfUrl, () => props.pageIndex],
  async () => {
    if (hasValidConfig.value && containerRef.value) await renderPDFPage()
  },
  { immediate: false },
)

onMounted(async () => {
  await nextTick()
  if (hasValidConfig.value && containerRef.value) {
    await renderPDFPage()

    // Debounced resize: render after settle for max quality
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null
    resizeObserver.value = new ResizeObserver(() => {
      if (!hasValidConfig.value || isLoading.value) return
      // Avoid altering opacity during initial load
      if (!isReady.value) return
      if (resizeTimeout) clearTimeout(resizeTimeout)
      if (containerRef.value) containerRef.value.style.opacity = '0.9'
      resizeTimeout = setTimeout(() => {
        renderPDFPage().then(() => {
          if (containerRef.value) {
            containerRef.value.style.transition = 'opacity 80ms ease'
            containerRef.value.style.opacity = '1'
          }
        })
      }, 120)
    })
    const el = containerRef.value
    if (el && el instanceof Element) {
      resizeObserver.value.observe(el)
    }
  }
})

onBeforeUnmount(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
    resizeObserver.value = null
  }
  isReady.value = false
  isLoading.value = false
  error.value = null
})
</script>
